<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026
  // Passiv start to dialog phase.
  // Puts written sentences on matching pictures
  // sentences shown above 4 pictures
  // moves down to pictures - and sentence read aloud
  // finger pointing while sentence is read aloud
  // sentence is divided and object hidden
  // repeated for the four sentences

  // OBS: pause button here is not to pause help, but to pause what is show automatically

  import { fly } from "svelte/transition";
  import { linear } from "svelte/easing";

  import { Pages, Path, Extension, LevelExtension, DaDK } from "../enums";

  import { firstWaitSecs } from "../constants";

  import { Icons } from "../utilities/icons";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { targetVerbAndObjects } from "../globals/targets";
  import { route } from "../stores/route.svelte";
  import { progress } from "../stores/progress.svelte";

  import { result } from "../stores/result.svelte";
  import { studentUserData } from "../stores/user-store.svelte";
  import { levelExtensionProgress } from "../stores/level-extension-progress.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // TYPES --------------------------------------------------

  const State = {
    Introduction: "introduction",
    IsPointing: "isPointing",
    Paused: "paused",
    ExitModalShown: "exitModalShown",
    WaitAfterEnd: "waitAfterEnd",
  } as const;

  type State = (typeof State)[keyof typeof State];

  // CONSTANTS --------------------------------------------

  const fingerPointDelaySecs = 800;
  const nextStepDelaySecs = 500;

  // STATE ------------------------------------------------

  let currentState: State = $state(State.Introduction);

  let pointFingerStage: 0 | 1 | 2 | 3 = $state(0); // 0-3 one pointFingerStage for each picture finger pointed to

  // arrays for send and received sentences
  // needed for animation to work

  let fingerPointing = $state([false, false, false, false]);
  let textTornApart = $state([false, false, false, false]); // for 2.2 not torn
  let objectHidden = $state([false, false, false, false]); // for 2.2 not hidden

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step8 currentState:", currentState);
  });

  // FUNCTIONS --------------------------------------------

  // pause before sentence is played
  const waitAndStart = (): void => {
    playIntro();
  };

  const playIntro = (): void => {
    if (levelExtensionProgress.value === LevelExtension.Object) {
      waitAfterIntro();
    } else if (levelExtensionProgress.value === LevelExtension.Verb) {
      playSoundWithAudioTag(audioTag, DaDK.Step8IntroVerb, waitAfterIntro);
    } else {
      playSoundWithAudioTag(audioTag, DaDK.Step8IntroSubject, waitAfterIntro);
    }
  };

  const waitAfterIntro = (): void => {
    setTimeout(() => {
      currentState = State.IsPointing;
      pointWithFinger();
    }, firstWaitSecs);
  };

  const pointWithFinger = (): void => {
    if (currentState !== State.IsPointing) {
      return;
    }
    // point
    fingerPointing = [false, false, false, false];
    fingerPointing[pointFingerStage] = true;
    fingerPointing = fingerPointing;
    if (levelExtensionProgress.value === LevelExtension.Subject) {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[pointFingerStage].sentenceFull,
        null,
      );
    } else {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[pointFingerStage].sentenceOK,
        null,
      );
    }
    setTimeout(() => {
      cutSentence();
    }, fingerPointDelaySecs);
  };

  const cutSentence = (): void => {
    if (currentState !== State.IsPointing) {
      return;
    }
    textTornApart[pointFingerStage] = true;
    setTimeout(
      () => {
        hideObject();
      },
      levelExtensionProgress.value === LevelExtension.Subject
        ? nextStepDelaySecs * 3
        : nextStepDelaySecs,
    );
  };

  const hideObject = (): void => {
    if (currentState !== State.IsPointing) {
      return;
    }
    objectHidden[pointFingerStage] = true;
    pointNextOrEnd();
  };

  const pointNextOrEnd = (): void => {
    if (currentState !== State.IsPointing) {
      return;
    }
    progress.value += 1;
    progress;
    pointFingerStage++;
    setTimeout(() => {
      if (pointFingerStage > 3) {
        setCompletedBackgroundColor(true);
        currentState = State.WaitAfterEnd;
        result.value.lastStepCompleted = 8;
        route.current = Pages.Step9;
      } else {
        pointWithFinger();
      }
    }, firstWaitSecs);
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      pointFingerStage = 0;
      fingerPointing = [false, false, false, false];
      textTornApart = [false, false, false, false];
      objectHidden = [false, false, false, false];
      currentState = State.IsPointing;
      pointWithFinger();
    } else {
      pauseStartTime = Date.now();
      currentState = State.Paused;
      fingerPointing = [false, false, false, false];
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    currentState = State.ExitModalShown;
    fingerPointing = [false, false, false, false];
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    pointFingerStage = 0;
    fingerPointing = [false, false, false, false];
    textTornApart = [false, false, false, false];
    if (levelExtensionProgress.value === LevelExtension.Verb) {
      objectHidden = [true, true, true, true];
    } else {
      objectHidden = [false, false, false, false];
    }
    currentState = State.IsPointing;
    pointWithFinger();
  };

  // INITIALIZE --------------------------------------------

  if (levelExtensionProgress.value === LevelExtension.Verb) {
    objectHidden = [true, true, true, true];
  }
  waitAndStart();
</script>

<!-- HTML ===================================================== -->

<main data-page="step8">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>

  <!-- top panel with button for help and space for text to fly in -->
  <div class="top-panel">
    <div>
      <div>
        <span class="stop-button">
          <RoundButton
            disabled={currentState === State.Paused}
            onclick={exitButtonClicked}
            icon={Icons.TrafficLightRed}
          />
        </span>
        <RoundButton
          disabled={currentState === State.ExitModalShown}
          onclick={pauseClicked}
          icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
        />
      </div>
    </div>
  </div>
  <!-- 4 images in 2x2 grid -->
  {#each targetVerbAndObjects.value as target, i}
    <div class={"image img" + i}>
      <div class="button-text">
        <!-- #each even though only one at a time - in order to be svelte animated  -->
        <!-- TODO: for 2.2/Level.SecondSubject subject has to fly in -->

        <!-- Not extenstion or first time through one of the extensions with object  -->
        {#if levelExtensionProgress.value === LevelExtension.Object}
          {#if textTornApart[i]}
            <div class="first-tab">
              {targetVerbAndObjects.value[i].verb}
            </div>
            <div class="second-tab" class:hide-object={objectHidden[i]}>
              {targetVerbAndObjects.value[i].object}
            </div>
          {:else}
            {targetVerbAndObjects.value[i].sentence}
          {/if}
        {:else if levelExtensionProgress.value === LevelExtension.Verb}
          <!-- Second and third time through with extension  -->
          <!-- Extension: Verb -->
          {#if textTornApart[i]}
            <div class="first-tab" class:hide-object={objectHidden[i]}>
              {targetVerbAndObjects.value[i].verb}
            </div>
            <div class="second-tab">
              {targetVerbAndObjects.value[i].object}
            </div>
          {:else}
            {targetVerbAndObjects.value[i].sentence}
          {/if}
        {:else}
          <!-- Extenstion: Subject -->
          {#if textTornApart[i]}
            <!-- TODO: subject fly in -->
            <div
              class="first-tab name-caps short-first-tab"
              transition:fly|global={{
                duration: 700,
                easing: linear,
                y: 700,
              }}
            >
              {targetVerbAndObjects.value[i].subjectName}
            </div>
            <div
              class="second-tab undotted long-second-tab"
              class:hide-object={objectHidden[i]}
            >
              {targetVerbAndObjects.value[i].sentence}
            </div>
          {:else}
            <!-- {targetVerbAndObjects.value[i].sentence} -->
          {/if}
        {/if}
      </div>
      <picture>
        <source
          type="image/webp"
          srcset={encodeURI(Path.Img + target.image + Extension.Webp)}
          draggable={false}
        />
        <img
          style="background-image: url('{encodeURI(
            Path.ImgBg + target.image + Extension.ImgBg,
          )}'); 
            background-image: image-set('{encodeURI(
            Path.ImgBg + target.image + Extension.WebpBg,
          )}'); 
            background-size: cover;"
          draggable={false}
          src={encodeURI(Path.Img + target.image + Extension.Png)}
          alt=""
        />
      </picture>
    </div>
    <div
      class={"finger-img finger-img-" + i}
      class:finger-point={fingerPointing[i]}
    >
      <picture class="finger-png">
        <source
          type="image/webp"
          srcset={encodeURI("finger-2.webp")}
          draggable={false}
        />
        <img draggable={false} src={"finger-2.png"} alt="" />
      </picture>
    </div>
  {/each}
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>
</main>

{#if currentState === State.Paused}
  <PauseMessage pauseMessageClicked={pauseClicked} />
{/if}

{#if currentState === State.ExitModalShown}
  <ExitModal yes={exitTask} no={dismissExitModal} />
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 49% 49%;
    grid-template-rows: 14% 41% 41% 2%;
    grid-template-areas:
      "top top"
      "img1 img2"
      "img3 img4"
      "prog prog";
    gap: 2%;
    padding: 3%;
    padding-top: 1%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .present-area {
    display: flex;
    flex-grow: 3;
    justify-content: center;
    align-items: center;
  }
  .image {
    border: solid 8px var(--button-border-disabled);
    background-color: white;
    padding: 0;
  }
  .img0 {
    grid-area: img1;
  }
  .img1 {
    grid-area: img2;
  }
  .img2 {
    grid-area: img3;
  }
  .img3 {
    grid-area: img4;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  img {
    width: 100%;
    height: 85%;
    object-fit: contain;
    object-position: bottom;
  }
  .sentence {
    border: solid 1px black;
    background: white;
    height: 8vh;
    width: 45vw;
    font-size: 6vmin;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
  }
  .wide-sentence {
    width: 65vw;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .button-text {
    border: solid 1px black;
    background: white;
    width: 100%;
    height: 15%;
    color: black;
    font-size: 3.5vmin;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
  }
  .first-tab {
    display: flex;
    height: 100%;
    width: 50%;
    align-items: center;
    justify-content: flex-end;
    padding-right: 0.5vmin;
  }
  .short-first-tab {
    width: 40%;
  }
  .second-tab {
    display: flex;
    height: 100%;
    width: 50%;
    border: none;
    border-left: 2px dotted black;
    align-items: center;
    justify-content: flex-start;
    padding-left: 0.5vmin;
  }
  .long-second-tab {
    width: 60%;
  }
  .undotted {
    border-left: none;
  }
  .hide-object {
    transform: rotateX(-180deg);
    color: lightGray;
    background: lightGray;
    transition:
      transform 0.5s,
      background-color 0.5s,
      color 0.1s;
    transform-style: preserve-3d;
  }
  .hide-subject {
    visibility: hidden;
  }
  .finger-img {
    z-index: 2;
    visibility: hidden;
    pointer-events: none;
    transform: translate(10vw, 5vh);
  }
  .finger-img-0 {
    grid-area: img1;
  }
  .finger-img-1 {
    grid-area: img2;
  }
  .finger-img-2 {
    grid-area: img3;
  }
  .finger-img-3 {
    grid-area: img4;
  }
  /* finger pointing help with animated finger sliding up */
  .finger-point {
    visibility: visible;
    animation-duration: 2s;
    animation-name: slideup;
  }
  .name-caps {
    text-transform: capitalize;
  }
  @keyframes slideup {
    from {
      transform: translate(10vw, 50vh);
    }
    to {
      transform: translate(10vw, 5vh);
    }
  }
</style>
